import { CardShell } from '@atlas/ui-components'
import { Button } from '@atlas/ui-primitives'

export interface CommitFooterProps {
	total: number
	readyCount: number
	onCommit: () => void
	isCommitting?: boolean
	/** Distinct failure messages from the files that did not upload. */
	errors?: string[]
	/**
	 * The classification registry has not resolved. Every file is unready anyway
	 * (nothing can be classified without axes), so the button is already disabled
	 * — this is what stops that reading as "you forgot to fill something in".
	 */
	axesUnavailable?: boolean
}

/** Footer: "N of M valid · K held back" summary + commit button (disabled at 0 ready). */
export function CommitFooter({
	total,
	readyCount,
	onCommit,
	isCommitting = false,
	errors = [],
	axesUnavailable = false,
}: CommitFooterProps) {
	const heldBack = total - readyCount
	return (
		<CardShell className="flex items-center justify-between gap-ds-3 p-ds-3">
			<div className="space-y-1">
				<p className="text-ds-sm text-ds-text-muted">
					{readyCount} of {total} valid
					{heldBack > 0 ? ` · ${heldBack} held back (missing classification)` : ''}
				</p>
				{/* The FILES' OWN failure reasons, not a generic line. "No files were
				    classified" described a consequence rather than a cause — and a
				    misleading one, since classification never ran: the upload failed
				    first, and the reason was already known here and thrown away. */}
				{errors.map((message) => (
					<p key={message} className="text-ds-sm text-ds-danger">
						Upload failed — {message}
					</p>
				))}
				{axesUnavailable && (
					<p className="text-ds-sm text-ds-danger">
						The classification axes could not be loaded, so nothing can be committed. Reload and try again.
					</p>
				)}
			</div>
			<Button variant="primary" onPress={onCommit} isDisabled={readyCount === 0 || isCommitting || axesUnavailable}>
				{isCommitting ? 'Committing…' : `Commit ${readyCount} ${readyCount === 1 ? 'file' : 'files'}`}
			</Button>
		</CardShell>
	)
}
